<template>
  <div
    class="p-6"
    @keydown="onKeydown">
    <!-- Title -->
    <div class="mb-5">
      <h2
        data-modal-title
        class="text-lg font-bold">
        {{ $t('W: Angiv pris') }}
      </h2>
      <p class="mt-1 text-sm">{{ lineName }}</p>
    </div>

    <!-- New price -->
    <div class="mb-5">
      <label
        for="resolve-unit-price"
        class="mb-1 block text-sm font-semibold">
        {{ $t('W: Stykpris') }}
      </label>
      <input
        id="resolve-unit-price"
        v-model="unitPrice"
        type="number"
        min="0"
        step="0.01"
        class="w-full rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-border-strong focus:outline-none" />
    </div>

    <!-- Reason -->
    <div class="mb-5">
      <label
        for="resolve-reason"
        class="mb-1 block text-sm font-semibold">
        {{ $t('W: Årsag') }}
      </label>
      <textarea
        id="resolve-reason"
        v-model="reason"
        rows="3"
        :placeholder="$t('W: f.eks. Aftalt pris med kunden, prisen mangler i betalers prissegment...')"
        class="w-full resize-none rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-border-strong focus:outline-none" />
    </div>

    <!-- Buttons -->
    <div class="flex justify-end gap-3">
      <UiBaseButton
        :text="$t('W: Gem pris')"
        :isDisabled="isSubmitDisabled"
        :isLoading="isSaving"
        @button-click="onSubmit" />
    </div>
  </div>
</template>

<script lang="ts">
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useModalStore } from '@/stores/modalStore'
import type { PropType } from 'vue'

export default {
  name: 'ResolveUnknownLinePriceModal',

  emits: ['close'],

  props: {
    lineName: { type: String, required: true },
    onSave: {
      type: Function as PropType<(unitPrice: number, reason: string) => void | Promise<void>>,
      required: true,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      modalEntryID: useModalStore().top?.id,

      unitPrice: '' as string | number,
      reason: '',
      isSaving: false,
    }
  },

  computed: {
    parsedUnitPrice(): number {
      return Number(this.unitPrice)
    },

    isSubmitDisabled(): boolean {
      if (this.isSaving) return true
      if (String(this.unitPrice).trim().length === 0) return true
      return Number.isNaN(this.parsedUnitPrice) || this.parsedUnitPrice < 0
    },
  },

  methods: {
    onKeydown(event: KeyboardEvent): void {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        this.onSubmit()
      }
    },

    async onSubmit(): Promise<void> {
      if (this.isSubmitDisabled) return
      this.isSaving = true
      try {
        // The handler does its own error handling (toast + inline error on the view) and never
        // rethrows — awaiting it here is only for the loading state and closing afterwards.
        await this.onSave(this.parsedUnitPrice, this.reason.trim())
      } finally {
        this.isSaving = false
      }
      this.$emit('close')
    },
  },

  created() {
    // A typed price plus the reason recorded with it.
    this.unsavedGuard.register({
      snapshot: () => ({ unitPrice: this.unitPrice, reason: this.reason }),
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
